'use client';

import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

interface ImageSectionProps {
  src: string | null | undefined;
  fallbackSrc?: string | null;
  alt?: string;
  aspectRatio?: 'square' | '16:9' | '4:3' | null;
  galleryUrls?: string[] | null;
}

export function ImageSection({
  src,
  fallbackSrc,
  alt = 'Image',
  aspectRatio = 'square',
  galleryUrls,
}: ImageSectionProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Build gallery from galleryUrls or fallback to single image
  const gallery = galleryUrls?.length
    ? galleryUrls
    : (src || fallbackSrc)
      ? [src || fallbackSrc!]
      : [];

  if (gallery.length === 0) {
    return null;
  }

  const aspectClass = {
    square: 'aspect-square',
    '16:9': 'aspect-video',
    '4:3': 'aspect-[4/3]',
  }[aspectRatio || 'square'];

  const hasMultiple = gallery.length > 1;

  return (
    <div className="space-y-2">
      {/* Main Image */}
      <div className={cn('relative bg-muted rounded overflow-hidden', aspectClass)}>
        <img
          src={gallery[selectedIndex]}
          alt={hasMultiple ? `${alt} - Image ${selectedIndex + 1}` : alt}
          className="w-full h-full object-contain"
        />
        {/* Navigation arrows for multiple images */}
        {hasMultiple && (
          <>
            <button
              onClick={() => setSelectedIndex((i) => (i === 0 ? gallery.length - 1 : i - 1))}
              className="absolute left-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-background/80 hover:bg-background transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setSelectedIndex((i) => (i === gallery.length - 1 ? 0 : i + 1))}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-background/80 hover:bg-background transition-colors"
              aria-label="Next image"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            {/* Image counter */}
            <div className="absolute bottom-1 right-1 px-2 py-0.5 rounded bg-background/80 text-xs">
              {selectedIndex + 1} / {gallery.length}
            </div>
          </>
        )}
      </div>
      {/* Thumbnail strip for multiple images */}
      {hasMultiple && (
        <div className="flex gap-1 overflow-x-auto pb-1">
          {gallery.map((url, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedIndex(idx)}
              className={cn(
                "flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition-colors",
                idx === selectedIndex
                  ? "border-primary"
                  : "border-transparent hover:border-muted-foreground/50"
              )}
            >
              <img
                src={url}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
